import React, { useState } from "react";
import { toast } from "sonner";
import { MapPin, Mail, User, ShieldCheck, Send, Phone } from "lucide-react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { api, formatApiErrorDetail } from "../lib/api";
import { TRUST } from "../data/content";

const ContactPage = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/contact", form);
      toast.success(data.message || "Message sent!");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <section className="bg-[#0F2942] py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="text-xs uppercase tracking-widest font-semibold text-amber-400">Contact</span>
          <h1 className="font-display mt-3 text-4xl sm:text-5xl font-extrabold text-white">Get In Touch</h1>
          <p className="mt-4 text-slate-300 max-w-2xl">
            Have a question, want to volunteer, or wish to support our work? We'd love to hear from you.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid lg:grid-cols-5 gap-10">
          {/* Info */}
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
              <h3 className="font-display text-lg font-bold text-slate-900 mb-4">Reach Us</h3>
              <ul className="space-y-4 text-sm">
                <li className="flex gap-3">
                  <MapPin className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" />
                  <span className="text-slate-600">{TRUST.address}</span>
                </li>
                <li className="flex gap-3 items-center">
                  <Mail className="h-5 w-5 text-emerald-600 shrink-0" />
                  <a href={`mailto:${TRUST.email}`} className="text-slate-600 hover:text-emerald-700">{TRUST.email}</a>
                </li>
                <li className="flex gap-3 items-center">
                  <User className="h-5 w-5 text-emerald-600 shrink-0" />
                  <span className="text-slate-600">President: {TRUST.president}</span>
                </li>
              </ul>
            </div>
            <div className="bg-[#0F2942] rounded-2xl p-6 text-white">
              <h3 className="font-display text-lg font-bold flex items-center gap-2 mb-4">
                <ShieldCheck className="h-5 w-5 text-amber-400" /> Verified & Registered
              </h3>
              <div className="space-y-2 text-sm text-slate-300">
                <p><span className="text-slate-400">Registration:</span> {TRUST.deedNo}</p>
                <p><span className="text-slate-400">Registered at:</span> {TRUST.registeredAt}</p>
                <p><span className="text-slate-400">PAN:</span> {TRUST.pan}</p>
                <p><span className="text-slate-400">Income Tax URN:</span> {TRUST.urn}</p>
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-3">
            <form onSubmit={submit} className="bg-white rounded-2xl p-7 border border-slate-100 shadow-sm space-y-5">
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <Label htmlFor="name">Full Name *</Label>
                  <Input id="name" required value={form.name} onChange={set("name")} placeholder="Your name"
                    data-testid="contact-form-name-input" className="mt-1.5" />
                </div>
                <div>
                  <Label htmlFor="email">Email Address *</Label>
                  <Input id="email" type="email" required value={form.email} onChange={set("email")} placeholder="you@email.com"
                    data-testid="contact-form-email-input" className="mt-1.5" />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <Label htmlFor="phone">Phone (optional)</Label>
                  <Input id="phone" value={form.phone} onChange={set("phone")} placeholder="+91 ..."
                    data-testid="contact-form-phone-input" className="mt-1.5" />
                </div>
                <div>
                  <Label htmlFor="subject">Subject</Label>
                  <Input id="subject" value={form.subject} onChange={set("subject")} placeholder="How can we help?"
                    data-testid="contact-form-subject-input" className="mt-1.5" />
                </div>
              </div>
              <div>
                <Label htmlFor="message">Message *</Label>
                <Textarea id="message" required rows={5} value={form.message} onChange={set("message")}
                  placeholder="Write your message..." data-testid="contact-form-message-input" className="mt-1.5" />
              </div>
              <button
                type="submit"
                disabled={loading}
                data-testid="contact-form-submit-btn"
                className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white font-semibold px-7 py-3 rounded-full transition-all"
              >
                <Send className="h-4 w-4" /> {loading ? "Sending..." : "Send Message"}
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
